import React, { useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Building2, CreditCard, Loader2, Copy, Check,
  CheckCircle2, AlertCircle, Send,
} from 'lucide-react';
import { API } from '@/config';

/**
 * PaymentMethodModal — shown before checkout to let user pick payment method.
 *
 * Props:
 *  - isOpen, onClose
 *  - amount, currency, packageName
 *  - onCardPayment(): called when user picks card (continues to existing Stripe flow)
 *  - bankTransferConfig: { package_id, username, custom_config } — used to initiate bank transfer
 */
export const PaymentMethodModal = ({
  isOpen, onClose, amount, currency = 'eur', packageName,
  onCardPayment, bankTransferConfig,
}) => {
  const [view, setView] = useState('select'); // 'select' | 'bank-details' | 'submitted'
  const [bankInfo, setBankInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');

  const reset = () => {
    setView('select');
    setBankInfo(null);
    setError('');
    setCopied('');
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    onClose();
  };

  const handleBankTransferStart = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(
        `${API}/payments/bank-transfer/initiate`,
        bankTransferConfig
      );
      setBankInfo(data);
      setView('bank-details');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Αποτυχία δημιουργίας πληρωμής';
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(''), 2000);
    } catch (e) {
      console.error('Copy failed', e);
    }
  };

  if (!isOpen) return null;

  const formatAmount = (a) => `${Number(a).toFixed(2)} €`;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[150] bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
        onClick={handleClose}
        data-testid="payment-method-modal"
      >
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 30, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-navy-100 border border-gold/30 rounded-2xl shadow-2xl max-w-2xl w-full my-8 overflow-hidden"
        >
          <div className="px-6 py-5 border-b border-slate-800 flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-white">
                {view === 'select' && 'Επιλέξτε Τρόπο Πληρωμής'}
                {view === 'bank-details' && 'Στοιχεία Τραπεζικής Μεταφοράς'}
                {view === 'submitted' && 'Επιτυχία!'}
              </h3>
              <p className="text-sm text-slate-400 mt-1">
                {packageName} • <span className="text-gold font-medium">{formatAmount(amount)}</span>
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={loading}
              className="text-slate-400 hover:text-white p-1"
              aria-label="Close"
              data-testid="payment-modal-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {view === 'select' && (
            <div className="p-6 space-y-3">
              {/* Bank Transfer */}
              <button
                onClick={handleBankTransferStart}
                disabled={loading}
                className="w-full flex items-start gap-4 p-5 bg-navy-200/50 border-2 border-gold/30 rounded-xl hover:border-gold hover:bg-navy-200 transition-all text-left disabled:opacity-50"
                data-testid="payment-method-bank-btn"
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-gold" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-semibold text-white">Τραπεζική Μεταφορά</h4>
                    <span className="px-2 py-0.5 text-xs bg-green-500/20 text-green-400 border border-green-500/30 rounded-full font-medium">
                      0% Fees
                    </span>
                    <span className="px-2 py-0.5 text-xs bg-blue-500/15 text-blue-400 border border-blue-500/30 rounded-full">
                      Συνιστάται
                    </span>
                  </div>
                  <p className="text-sm text-slate-400 mt-1">
                    Δωρεάν μέσω IBAN. Επιβεβαίωση 1-2 εργάσιμες ημέρες.
                  </p>
                </div>
                {loading ? (
                  <Loader2 className="w-5 h-5 text-gold animate-spin flex-shrink-0 mt-1" />
                ) : null}
              </button>

              {/* Card payment */}
              <button
                onClick={onCardPayment}
                disabled={loading}
                className="w-full flex items-start gap-4 p-5 bg-navy-200/50 border-2 border-slate-700 rounded-xl hover:border-slate-500 hover:bg-navy-200 transition-all text-left disabled:opacity-50"
                data-testid="payment-method-card-btn"
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-slate-700/50 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-slate-200" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-semibold text-white">Κάρτα (Visa / Mastercard)</h4>
                    <span className="px-2 py-0.5 text-xs bg-amber-500/15 text-amber-400 border border-amber-500/30 rounded-full">
                      Άμεσα
                    </span>
                  </div>
                  <p className="text-sm text-slate-400 mt-1">
                    Ασφαλής πληρωμή μέσω Stripe. Apple Pay & Google Pay υποστηρίζονται.
                  </p>
                </div>
              </button>

              {error && (
                <div className="px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-300 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}
            </div>
          )}

          {view === 'bank-details' && bankInfo && (
            <div className="p-6 space-y-4">
              <div className="bg-gradient-to-br from-gold/10 to-amber-600/10 border border-gold/30 rounded-xl p-4">
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Κωδικός Αναφοράς</p>
                <div className="flex items-center justify-between">
                  <code className="text-2xl font-mono font-bold text-gold tracking-wider" data-testid="bank-reference-code">
                    {bankInfo.reference_code}
                  </code>
                  <button
                    onClick={() => copyToClipboard(bankInfo.reference_code, 'ref')}
                    className="p-2 hover:bg-gold/20 rounded-lg transition-colors"
                    data-testid="copy-reference-btn"
                  >
                    {copied === 'ref' ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-gold" />}
                  </button>
                </div>
                <p className="text-xs text-slate-300 mt-2">
                  ⚠️ Παρακαλώ συμπεριλάβετε αυτόν τον κωδικό στις παρατηρήσεις της μεταφοράς για άμεση ταυτοποίηση.
                </p>
              </div>

              <div className="bg-navy-200/50 border border-slate-700 rounded-xl p-4 space-y-3">
                <DetailRow label="Δικαιούχος" value={bankInfo.bank_details.beneficiary} onCopy={(v) => copyToClipboard(v, 'beneficiary')} copied={copied === 'beneficiary'} />
                <DetailRow label="IBAN" value={bankInfo.bank_details.iban} mono onCopy={(v) => copyToClipboard(v, 'iban')} copied={copied === 'iban'} />
                {bankInfo.bank_details.bank_name && (
                  <DetailRow label="Τράπεζα" value={bankInfo.bank_details.bank_name} />
                )}
                {bankInfo.bank_details.swift_bic && (
                  <DetailRow label="SWIFT/BIC" value={bankInfo.bank_details.swift_bic} mono onCopy={(v) => copyToClipboard(v, 'swift')} copied={copied === 'swift'} />
                )}
                <DetailRow label="Ποσό" value={formatAmount(bankInfo.amount)} highlight />
              </div>

              {bankInfo.bank_details.payment_notes && (
                <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-3 text-sm text-slate-300 whitespace-pre-wrap">
                  💡 {bankInfo.bank_details.payment_notes}
                </div>
              )}

              <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 text-xs text-slate-300">
                Μετά την αποστολή της μεταφοράς, ο διαχειριστής θα την επιβεβαιώσει εντός 1-2 εργάσιμων ημερών και θα λάβετε ειδοποίηση επιβεβαίωσης.
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleClose}
                  className="flex-1 px-4 py-3 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700/30"
                  data-testid="bank-close-btn"
                >
                  Κλείσιμο
                </button>
                <button
                  onClick={() => setView('submitted')}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-gold to-amber-600 text-navy-200 font-bold rounded-lg hover:from-amber-500 hover:to-gold inline-flex items-center justify-center gap-2"
                  data-testid="bank-confirm-btn"
                >
                  <Send className="w-4 h-4" />
                  Έκανα τη Μεταφορά
                </button>
              </div>
            </div>
          )}

          {view === 'submitted' && (
            <div className="p-8 text-center">
              <CheckCircle2 className="w-16 h-16 text-green-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Καταχωρήθηκε επιτυχώς!</h3>
              <p className="text-slate-300 mb-1">
                Το αίτημά σας με κωδικό αναφοράς:
              </p>
              <code className="text-gold font-mono text-lg block mb-4">{bankInfo?.reference_code}</code>
              <p className="text-sm text-slate-400 mb-6">
                Μόλις λάβουμε τη μεταφορά, θα ενεργοποιηθεί η συνδρομή σας και θα ενημερωθείτε με ειδοποίηση.
              </p>
              <button
                onClick={handleClose}
                className="px-6 py-2 bg-gold text-navy-200 font-bold rounded-lg hover:bg-amber-400"
                data-testid="bank-done-btn"
              >
                Κλείσιμο
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const DetailRow = ({ label, value, mono, highlight, onCopy, copied }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="text-xs text-slate-400 uppercase tracking-wider">{label}</span>
    <div className="flex items-center gap-2">
      <span className={`text-sm ${mono ? 'font-mono' : ''} ${highlight ? 'text-gold font-bold' : 'text-white'}`}>
        {value}
      </span>
      {onCopy && (
        <button onClick={() => onCopy(value)} className="p-1 hover:bg-slate-700/50 rounded">
          {copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3 text-slate-400" />}
        </button>
      )}
    </div>
  </div>
);

export default PaymentMethodModal;
